/**
 * ASCII Art Generator - 预设管理器
 *
 * 管理预设的创建、保存、加载、删除等操作
 */

import type { Preset, PresetMeta, PresetManagerConfig } from './types'
import { DEFAULT_PRESET_MANAGER_CONFIG } from './types'
import type { FullRenderSettings } from '../types'
import type { EffectsState } from '../effects/types'

// ============================================
// 预设管理器类
// ============================================

/**
 * 预设管理器
 * 管理内置预设和用户预设
 */
export class PresetManager {
  private config: PresetManagerConfig
  private builtinPresets: Preset[] = []
  private userPresets: Preset[] = []

  constructor(config: Partial<PresetManagerConfig> = {}) {
    this.config = { ...DEFAULT_PRESET_MANAGER_CONFIG, ...config }
    this.loadFromStorage()
  }

  // ============================================
  // 内置预设管理
  // ============================================

  /**
   * 注册内置预设
   */
  registerBuiltinPreset(preset: Preset): void {
    if (preset.category !== 'builtin') {
      console.warn('Cannot register non-builtin preset as builtin')
      return
    }
    if (this.builtinPresets.find(p => p.id === preset.id)) {
      console.warn(`Builtin preset ${preset.id} already exists`)
      return
    }
    this.builtinPresets.push(preset)
  }

  /**
   * 批量注册内置预设
   */
  registerBuiltinPresets(presets: Preset[]): void {
    presets.forEach(p => this.registerBuiltinPreset(p))
  }

  /**
   * 获取所有内置预设
   */
  getBuiltinPresets(): Preset[] {
    return [...this.builtinPresets]
  }

  // ============================================
  // 用户预设管理
  // ============================================

  /**
   * 创建用户预设
   */
  createPreset(
    name: string,
    settings: FullRenderSettings,
    effectsState: EffectsState,
    description: string = ''
  ): Preset {
    return {
      id: this.generateId(),
      name,
      description,
      category: 'user',
      createdAt: Date.now(),
      settings: { ...settings },
      effectsState: JSON.parse(JSON.stringify(effectsState)),
    }
  }

  /**
   * 添加用户预设
   */
  addUserPreset(preset: Preset): boolean {
    if (preset.category !== 'user') {
      console.warn('Cannot add non-user preset as user preset')
      return false
    }

    // 检查数量限制
    if (this.userPresets.length >= this.config.maxUserPresets) {
      console.warn('Maximum user presets reached')
      return false
    }

    // 检查重名
    if (this.userPresets.find(p => p.name === preset.name)) {
      console.warn(`Preset with name "${preset.name}" already exists`)
      return false
    }

    this.userPresets.push(preset)
    this.saveToStorage()
    return true
  }

  /**
   * 删除用户预设
   */
  removeUserPreset(presetId: string): boolean {
    const index = this.userPresets.findIndex(p => p.id === presetId)
    if (index === -1) {
      return false
    }
    this.userPresets.splice(index, 1)
    this.saveToStorage()
    return true
  }

  /**
   * 更新用户预设
   */
  updateUserPreset(presetId: string, updates: Partial<Pick<Preset, 'name' | 'description' | 'settings' | 'effectsState'>>): boolean {
    const preset = this.userPresets.find(p => p.id === presetId)
    if (!preset) {
      return false
    }

    if (updates.name !== undefined) preset.name = updates.name
    if (updates.description !== undefined) preset.description = updates.description
    if (updates.settings !== undefined) preset.settings = { ...updates.settings }
    if (updates.effectsState !== undefined) preset.effectsState = JSON.parse(JSON.stringify(updates.effectsState))

    this.saveToStorage()
    return true
  }

  /**
   * 获取所有用户预设
   */
  getUserPresets(): Preset[] {
    return [...this.userPresets]
  }

  // ============================================
  // 通用操作
  // ============================================

  /**
   * 获取所有预设
   */
  getAllPresets(): Preset[] {
    return [...this.builtinPresets, ...this.userPresets]
  }

  /**
   * 根据ID获取预设
   */
  getPresetById(presetId: string): Preset | undefined {
    return this.builtinPresets.find(p => p.id === presetId) ||
           this.userPresets.find(p => p.id === presetId)
  }

  /**
   * 根据名称获取预设
   */
  getPresetByName(name: string): Preset | undefined {
    return this.builtinPresets.find(p => p.name === name) ||
           this.userPresets.find(p => p.name === name)
  }

  /**
   * 获取预设元数据列表
   */
  getPresetMetaList(): PresetMeta[] {
    return this.getAllPresets().map(p => ({
      id: p.id,
      name: p.name,
      description: p.description,
      category: p.category,
      createdAt: p.createdAt,
      hasThumbnail: !!p.thumbnail,
    }))
  }

  /**
   * 检查预设是否存在
   */
  hasPreset(presetId: string): boolean {
    return !!this.getPresetById(presetId)
  }

  /**
   * 清除所有用户预设
   */
  clearUserPresets(): void {
    this.userPresets = []
    this.saveToStorage()
  }

  // ============================================
  // 存储操作
  // ============================================

  /**
   * 从本地存储加载用户预设
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.config.storageKey)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed)) {
          // 验证预设格式
          this.userPresets = parsed.filter(p => this.validatePreset(p))
        }
      }
    } catch (error) {
      console.error('Failed to load presets from storage:', error)
      this.userPresets = []
    }
  }

  /**
   * 保存用户预设到本地存储
   */
  private saveToStorage(): void {
    if (!this.config.autoSave) return

    try {
      localStorage.setItem(this.config.storageKey, JSON.stringify(this.userPresets))
    } catch (error) {
      console.error('Failed to save presets to storage:', error)
    }
  }

  // ============================================
  // 工具方法
  // ============================================

  /**
   * 生成唯一ID
   */
  private generateId(): string {
    return `preset_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * 验证预设格式
   */
  private validatePreset(preset: any): preset is Preset {
    return (
      typeof preset === 'object' &&
      typeof preset.id === 'string' &&
      typeof preset.name === 'string' &&
      typeof preset.category === 'string' &&
      preset.category === 'user' &&
      typeof preset.settings === 'object' &&
      typeof preset.effectsState === 'object'
    )
  }
}

// ============================================
// 单例实例
// ============================================

let presetManagerInstance: PresetManager | null = null

/**
 * 获取预设管理器单例
 */
export function getPresetManager(): PresetManager {
  if (!presetManagerInstance) {
    presetManagerInstance = new PresetManager()
  }
  return presetManagerInstance
}

/**
 * 创建新的预设管理器实例
 */
export function createPresetManager(config?: Partial<PresetManagerConfig>): PresetManager {
  return new PresetManager(config)
}